import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  buildCalendarCells,
  formatDateDisplay,
  formatMonthTitle,
  shiftYearMonth,
  WEEK_LABELS,
} from "../../utils/playbackUtils";

import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "./PlaybackIcons";

export default function DateCalendarPicker({
  selectedDate,
  onChange,
  recordedDates,
  visibleMonth,
  onVisibleMonthChange,
}) {
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocClick);

    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const calendarCells = useMemo(
    () => buildCalendarCells(visibleMonth),
    [visibleMonth]
  );

  const recordedSet = useMemo(
    () => new Set(recordedDates || []),
    [recordedDates]
  );

  const openCalendar = () => {
    setOpen((prev) => !prev);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={openCalendar}
        className="flex h-[52px] w-full items-center justify-center rounded-[18px] border border-gray-300 bg-white px-4 pr-12 text-center text-[16px] font-medium text-gray-900 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white sm:h-11 sm:rounded-xl sm:text-[15px]"
      >
        <span className="pointer-events-none select-none">
          {formatDateDisplay(selectedDate)}
        </span>

        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 sm:right-3">
          <CalendarIcon />
        </span>
      </button>

      {open && (
        <div className="absolute left-0 top-[calc(100%+8px)] z-50 w-[290px] rounded-2xl border border-gray-200 bg-white p-3 shadow-xl dark:border-gray-700 dark:bg-[#111827]">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() =>
                onVisibleMonthChange(shiftYearMonth(visibleMonth, -1))
              }
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-[#1F2937] dark:text-gray-100"
            >
              <ChevronLeftIcon />
            </button>

            <div className="text-sm font-bold">
              {formatMonthTitle(visibleMonth)}
            </div>

            <button
              type="button"
              onClick={() =>
                onVisibleMonthChange(shiftYearMonth(visibleMonth, 1))
              }
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-[#1F2937] dark:text-gray-100"
            >
              <ChevronRightIcon />
            </button>
          </div>

          <div className="mb-2 grid grid-cols-7 gap-1">
            {WEEK_LABELS.map((label) => (
              <div
                key={label}
                className="py-1 text-center text-[11px] font-semibold text-gray-500 dark:text-gray-400"
              >
                {label}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {calendarCells.map((dateValue, idx) => {
              if (!dateValue) {
                return <div key={`empty-${idx}`} className="h-9" />;
              }

              const isSelected = dateValue === selectedDate;
              const hasRecord = recordedSet.has(dateValue);

              return (
                <button
                  key={dateValue}
                  type="button"
                  onClick={() => {
                    onChange(dateValue);
                    setOpen(false);
                  }}
                  className={`relative h-9 rounded-lg text-sm font-medium transition ${
                    isSelected
                      ? "bg-blue-600 text-white"
                      : hasRecord
                      ? "bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-[#0F274A] dark:text-blue-200"
                      : "text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-[#1F2937]"
                  }`}
                >
                  {Number(dateValue.slice(-2))}

                  {hasRecord && !isSelected && (
                    <span className="absolute bottom-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-blue-600 dark:bg-blue-300" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
            <span>파란색 표시 = 녹화 있음</span>
            <span>{Array.isArray(recordedDates) ? recordedDates.length : 0}일</span>
          </div>
        </div>
      )}
    </div>
  );
}